import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPatch, operationsClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { reminderPolicyKeys } from '@/lib/queryKeys';
import type {
  ReminderPolicy,
  ReminderType,
  UpdateReminderPolicyDto,
} from '@/types/reminder-policy';

interface UpdateVariables {
  type: ReminderType;
  dto: UpdateReminderPolicyDto;
}

/**
 * Updates a `ReminderPolicy`. Invalidates list + detail caches on success.
 *
 * Backend: `PATCH /v2/reminder-policies/:type` →
 * `apps/operations/src/modules/reminders/policy/reminder-policy.controller.ts#update`.
 */
export function useUpdateReminderPolicy() {
  const queryClient = useQueryClient();

  return useApiMutation<ReminderPolicy, UpdateVariables>({
    mutationFn: ({ type, dto }) =>
      apiPatch<ReminderPolicy, UpdateReminderPolicyDto>(
        operationsClient,
        endpoints.reminderPoliciesV2.update(type),
        dto,
      ),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(reminderPolicyKeys.detail(variables.type), data);
      void queryClient.invalidateQueries({ queryKey: reminderPolicyKeys.lists() });
      toast.success('Reminder policy saved');
    },
  });
}
